import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useRemoveCartItemMutation } from "../../cart/api/cartApi";
import { useAddSavedForLaterItemMutation } from "../api/savedForLaterApi";
import { useGetCartQuery } from "../../cart/api/cartApi";

/**
 * Moves an item from Cart to Saved For Later via frontend orchestration.
 */
export const useMoveToSavedForLaterFromCart = () => {
  const { t } = useTranslation();
  const [addSavedItem] = useAddSavedForLaterItemMutation();
  const [removeCartItem] = useRemoveCartItemMutation();
  const { data: cart } = useGetCartQuery();

  const [isMoving, setIsMoving] = useState(false);

  const handleMoveToSavedForLater = useCallback(
    async (key: string) => {
      const item = cart?.items.find((i) => i.key === key);
      if (!item) return;

      setIsMoving(true);
      const toastId = toast.loading(
        t("cartItemActions.saving", "Saving for later...")
      );

      try {
        // Step 1: Add to Saved List (Use variation ID if it exists, otherwise product ID)
        // logic: item.id is already the specific ID (variation or simple) from the transformation
        await addSavedItem(item.id).unwrap();

        // Step 2: Remove from Cart
        await removeCartItem({ key }).unwrap();

        toast.success(
          t("feedback.savedForLater.addedSuccess", "Saved for later!"),
          {
            id: toastId,
            icon: "💾", // Optional visual distinction
          }
        );
      } catch (error) {
        console.error("Save for later failed", error);
        toast.error(t("feedback.savedForLater.error", "Failed to save item."), {
          id: toastId,
        });
      } finally {
        setIsMoving(false);
      }
    },
    [addSavedItem, removeCartItem, cart, t]
  );

  return { handleMoveToSavedForLater, isMoving };
};
